import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { customStyles } from "../components/CustomSelectStyles";
import {
  setInitialState,
  updateAppointment,
  updateDoctor,
  updateFacility,
  updateSpecialization,
} from "../Redux/Reducers/appointmentFilterSlice";
import {
  getAllDoctorData,
  getAvailableDoctorList,
  getFacility,
  getLookupByName,
  getSelectedEmployeeData,
  getSlotData,
} from "../utils/api";
import AppointmentDate from "./appointmentDate";
import { searchSvg } from "../assets/svg";
import {
  addMinutesToTime,
  compareWithCurrentDateTime,
  createOption,
  formattedDate,
} from "../utils/utilities";
import BookingSummarySection from "./BookingSummarySection";
import VerificationSection from "./VerificationSection";
import RegisteredPatientForm from "./RegisteredPatientForm";
import { toast } from "react-toastify";
import DoctorListSection from "./DoctorListSection";
import NewPatientForm from "./NewPatientForm";
import Bottombar from "./Bottombar";

function MidSection() {
  const selectedDateISOString = useSelector(
    (state) => state.appointment.selectedDate
  );

  const selectedDate = useMemo(
    () => new Date(selectedDateISOString),
    [selectedDateISOString]
  );

  const [slotData, setSlotData] = useState([]);
  const [facilityList, setFacilityList] = useState([]);
  const [specializationList, setSpecializationList] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [activeSection, setActiveSection] = useState("filter");
  const [doctorShowList, setDoctorShowList] = useState([]);

  const isMobile = window.innerWidth <= 768;

  const dispatch = useDispatch();
  const {
    selectedDoctor,
    selectedFacility,
    selectedSpecialization,
    selectedAppointment,
    isOtpVerified,
    doctorData,
  } = useSelector((state) => state.appointment) || {};
  const { primaryBackground } = useSelector((state) => state.dynamicStore);

  const [showForm, setShowForm] = useState("");
  const [doctorSelected, setDoctorSelected] = useState(null);

  useEffect(() => {
    setFacilityList(doctorData?.[0].facilities);
    setSpecializationList(
      doctorData?.[0].specialization?.map((item) => ({
        value: item,
        label: item,
      }))
    );
  }, [doctorData]);

  useEffect(() => {
    let timeoutId;
    if (isOtpVerified) {
      timeoutId = setTimeout(() => {
        setShowForm("registered");
      }, 2000);
    } else {
      setShowForm("");
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOtpVerified]);

  const selectValues = [
    {
      label: "Facility",
      option: facilityList,
      value: selectedFacility,
      placeholder: "--select facility--",
      onChange: (selected) => dispatch(updateFacility(selected)),
    },
    {
      label: "Specialization",
      option: specializationList,
      value: selectedSpecialization,
      placeholder: "--select specialization--",
      onChange: (selected) => dispatch(updateSpecialization(selected)),
    },
    {
      label: "Doctor",
      option: doctorList,
      value: doctorSelected,
      placeholder: "--select doctor--",
      onChange: (selected) => setDoctorSelected(selected),
    },
  ];

  const fetchSlotData = async () => {
    if (
      selectedDoctor &&
      selectedDate &&
      selectedFacility &&
      selectedSpecialization
    ) {
      const doctor = await getSlotData(
        [selectedDoctor?.value],
        [formattedDate(selectedDate)],
        selectedFacility,
        selectedSpecialization
      );
      if (doctor[0]?.data.length > 0) {
        setSlotData(doctor[0]?.data);
      } else {
        setSlotData([]);
        toast.error("No slots available.");
      }
    } else {
      setSlotData([]);
      toast.error("Please select all the fields.");
    }
  };

  useEffect(() => {
    if (
      selectedDoctor &&
      selectedDate &&
      selectedFacility &&
      selectedSpecialization
    ) {
      fetchSlotData();
    } else {
      setSlotData([]);
    }
  }, [selectedDoctor]);

  const fetchData = async () => {
    const [specialization, facility] = await Promise.all([
      getLookupByName({ lookupName: "Speciality" }),
      getFacility(),
    ]);
    setSpecializationList(
      specialization?.map((item) => createOption({ label: item.value }))
    );
    setFacilityList(
      facility?.map((item) => ({
        value: item.facility_id,
        label: item.facility_name,
      }))
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchDoctor = async () => {
      const doctor = await getAllDoctorData(
        selectedFacility?.label ?? "All",
        selectedSpecialization?.label ?? "All"
      );
      setDoctorList(
        doctor?.map((item) => ({
          value: item.employee_id,
          label:
            "Dr. " + (item.first_name ?? "") + " " + (item.last_name ?? ""),
          specialization: item.specialization,
        }))
      );
    };

    fetchDoctor();
    setDoctorShowList([]);
    setSlotData([]);
    dispatch(updateDoctor(null));
  }, [selectedFacility, selectedSpecialization]);

  const resetAllData = () => {
    setDoctorShowList([]);
    setSlotData([]);
    dispatch(
      updateAppointment({
        time: null,
        date: null,
        duration: null,
        identification: null,
        type: null,
      })
    );
  };

  useEffect(() => {
    async function fetchData() {
      resetAllData();
      if (
        doctorSelected &&
        selectedFacility === null &&
        selectedSpecialization === null
      ) {
        const res = await getSelectedEmployeeData(doctorSelected?.value);
        if (res?.statusCode === 200) {
          setSpecializationList(
            res?.data[0]?.specialization?.map((item) => ({
              value: item,
              label: item,
            }))
          );
          setFacilityList(res?.data[0]?.facilities);
        }
        console.log(res, "res");
      }
    }
    fetchData();
  }, [doctorSelected]);

  const getAvailableDoctors = async () => {
    if (selectedFacility && selectedSpecialization) {
      const doctor = await getAvailableDoctorList(
        selectedFacility?.label,
        selectedSpecialization?.label,
        formattedDate(selectedDate),
        doctorSelected?.value ?? undefined
      );
      if (doctor?.statusCode === 200) {
        setDoctorShowList(
          doctor?.data?.map((item) => ({
            value: item.employee_id,
            label:
              "Dr. " + (item.first_name ?? "") + " " + (item.last_name ?? ""),
            specialization: item.specialization,
            experience: item.experience,
            qualification: item.qualification,
            image: item.undefined || item.profile || "",
            facility: item.facility,
            Fee: 350,
            slot_time: item.slot_time,
          }))
        );
      } else {
        setDoctorShowList([]);
        if (doctorSelected?.value) {
          toast.error("Doctor is not available");
          return;
        }
        toast.error("No doctors available for the selected date");
      }
    } else {
      setDoctorShowList([]);
      toast.error("Please select facility and specialization");
    }
  };

  useEffect(() => {
    if (selectedSpecialization && selectedFacility) {
      getAvailableDoctors();
    }
    setDoctorShowList([]);
    dispatch(updateDoctor(null));
  }, [selectedDate]);

  return (
    <div className="mid-section-container position-relative">
      {activeSection === "filter" && (
        <>
          <div className="mid-top-section">
            <div className="mid-header">
              <div>Book an &nbsp;</div>Appointment
            </div>
            <div className="f500-14px">
              Schedule your appointment with our best healthcare providers
            </div>
          </div>
          <div className="mid-bottom-section">
            <div className="filter-section pt-lg-5">
              <div className="filter-select-section gap-lg-5">
                {selectValues?.map((select, index) => (
                  <div className="col">
                    <label className="f500-16px">{select.label}</label>
                    <span className="text-danger" style={{ fontSize: "16px" }}>
                      *
                    </span>
                    <Select
                      options={select.option}
                      placeholder={select.placeholder}
                      value={select.value}
                      onChange={select.onChange}
                      styles={customStyles}
                    />
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-center gap-3">
                <div
                  className="f500-16px cursor-pointer reset-button"
                  onClick={() => {
                    setSlotData([]);
                    fetchData();
                    setDoctorSelected(null);
                    dispatch(setInitialState());
                  }}
                >
                  Reset changes
                </div>
                <div
                  className="f500-16px cursor-pointer search-button"
                  style={{
                    backgroundColor: selectedSpecialization
                      ? ""
                      : "rgba(186, 186, 186, 1)",
                    border: selectedSpecialization
                      ? ""
                      : "1px solid rgba(186, 186, 186, 1)",
                  }}
                  onClick={() => {
                    getAvailableDoctors();
                  }}
                >
                  {searchSvg({})}
                  Search
                </div>
              </div>
            </div>
            <div className="appointment-section px-lg-4">
              {selectedSpecialization && <AppointmentDate />}
              {doctorShowList.length > 0 && (
                <DoctorListSection doctorList={doctorShowList} />
              )}
              <div className="d-flex justify-content-between">
                <div className="slot-section">
                  {slotData.length > 0 && selectedDoctor && (
                    <div>
                      <div className="f500-16px">Available Slots</div>
                      <div
                        className="d-flex gap-2 gap-lg-3 flex-wrap p-2 p-lg-3"
                        style={{
                          boxShadow:
                            "0px 0px 4px 0px rgba(0, 0, 0, 0.25) inset",
                          borderRadius: "6px",
                        }}
                      >
                        {slotData?.map((slot, index) => (
                          <div>
                            {slot?.status && (
                              <button
                                key={index}
                                className={`f500-14px text-center ${
                                  selectedAppointment?.time === slot?.time &&
                                  selectedAppointment?.date ===
                                    formattedDate(selectedDate)
                                    ? "selected-slot-button"
                                    : slot.status === "Available"
                                    ? "slot-button"
                                    : "slot-button-disabled"
                                }`}
                                onClick={() => {
                                  if (slot.status === "Available") {
                                    if (
                                      compareWithCurrentDateTime(
                                        formattedDate(selectedDate),
                                        addMinutesToTime(
                                          slot?.time,
                                          slot?.duration
                                        )
                                      )
                                    ) {
                                      dispatch(
                                        updateAppointment({
                                          time: slot?.time,
                                          date: formattedDate(selectedDate),
                                          duration: slot?.duration,
                                          identification:
                                            slot?.appointment_identification,
                                          type: slot?.appointment_type,
                                        })
                                      );
                                    } else {
                                      toast.error(
                                        "You can't select past time slot"
                                      );
                                    }
                                  }
                                }}
                              >
                                {slot.time}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="f500-14px d-flex gap-4 mt-2">
                        <div className="d-flex align-items-center gap-1">
                          <div
                            style={{
                              height: "12px",
                              width: "12px",
                              borderRadius: "50%",
                              backgroundColor: "rgba(219, 219, 219, 1)",
                              border: "1px solid rgba(84, 100, 99, 1)",
                            }}
                          ></div>
                          Not Available
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <div
                            style={{
                              height: "12px",
                              width: "12px",
                              borderRadius: "50%",
                              backgroundColor: "rgba(255, 255, 255, 1)",
                              border: "1px solid rgba(84, 100, 99, 1)",
                            }}
                          ></div>
                          Available
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <div
                            style={{
                              height: "12px",
                              width: "12px",
                              borderRadius: "50%",
                              backgroundColor: primaryBackground,
                              border: "1px solid rgba(84, 100, 99, 1)",
                            }}
                          ></div>
                          Selected Slot
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* {selectedDoctor && <DoctorTimingSection />} */}
              </div>
              {selectedAppointment?.time && (
                <div className="d-flex justify-content-center">
                  <div
                    className="next-button mb-4"
                    onClick={() => {
                      if (
                        compareWithCurrentDateTime(
                          selectedAppointment?.date,
                          addMinutesToTime(
                            selectedAppointment?.time,
                            selectedAppointment?.duration
                          )
                        )
                      ) {
                        setActiveSection("booking");
                      } else {
                        toast.error("You can't select past time slot");
                      }
                    }}
                  >
                    Next
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {activeSection === "booking" && (
        <div>
          <div
            className="mid-top-section"
            style={{ height: isMobile ? "147px" : "115px" }}
          ></div>
          <div className="mid-bottom-section">
            <BookingSummarySection setActiveSection={setActiveSection} />
            {showForm === "registered" || showForm === "payment" ? (
              <RegisteredPatientForm
                setShowForm={setShowForm}
                showForm={showForm}
              />
            ) : showForm === "unregistered" || showForm === "payment" ? (
              <NewPatientForm setShowForm={setShowForm} showForm={showForm} />
            ) : showForm === "" ? (
              <VerificationSection setShowForm={setShowForm} />
            ) : null}
          </div>
        </div>
      )}
      {/* <Bottombar /> */}
    </div>
  );
}

export default MidSection;
